import React from 'react';
import { Users, Wallet, AlertCircle, X } from 'lucide-react';
import useGameStore from '../../store/gameStore';

/**
 * Emergency Assemblée Générale Extraordinaire modal.
 * Offers the player a one-shot recapitalization: pay personal contribution
 * (50 % of the equity deficit) → treasury bumped + streak reset.
 *
 * Pops automatically when bankruptcyAlertStage transitions to 'red', OR
 * via the banner button on 'rose' / 'red'.
 */
export default function EmergencyShareholderModal() {
  const pending = useGameStore(s => s.pendingShareholderMeeting);
  const accept = useGameStore(s => s.acceptEmergencyRecap);
  const decline = useGameStore(s => s.declineEmergencyRecap);
  const treasury = useGameStore(s => s.treasury || 0);

  if (!pending) return null;

  const { recapAmount, deficit, daysUntilLiquidation } = pending;

  return (
    <div
      className="fixed inset-0 bg-black/75 z-[110] flex items-center justify-center p-4 backdrop-blur-sm"
      data-testid="age-modal-overlay"
    >
      <div
        className="bg-ink-800 rounded-xl shadow-2xl w-full max-w-xl border-2 border-amber-500/40 overflow-hidden"
        data-testid="age-modal"
      >
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-amber-800/40 to-rose-900/40 border-b border-ink-600">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Users className="w-4 h-4 text-amber-300" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">
                  Assemblée Générale Extraordinaire
                </span>
              </div>
              <h2 className="text-xl font-bold text-slate-100">
                Recapitalisation — Dernière chance
              </h2>
            </div>
            <button
              onClick={decline}
              className="p-1 rounded hover:bg-white/10 text-slate-400"
              data-testid="age-close"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-950/40 border border-rose-500/40">
            <AlertCircle className="w-5 h-5 text-rose-300 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-100 leading-relaxed">
              Votre société sera placée en liquidation judiciaire dans{' '}
              <strong className="text-rose-200">{daysUntilLiquidation} jour{daysUntilLiquidation > 1 ? 's' : ''}</strong>{' '}
              sans redressement. Les actionnaires peuvent voter un apport personnel
              pour combler une partie du déficit. <strong className="text-rose-200">Cette opération ne peut être réalisée qu'une seule fois par partie.</strong>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-ink-700/40 border border-ink-600">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Déficit de capitaux propres</div>
              <div className="text-lg font-bold text-rose-300 font-mono" data-testid="age-deficit">
                {deficit.toLocaleString('fr-FR')} €
              </div>
            </div>
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">Apport personnel requis (50 %)</div>
              <div className="text-lg font-bold text-emerald-200 font-mono" data-testid="age-recap-amount">
                {recapAmount.toLocaleString('fr-FR')} €
              </div>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-ink-700/30 border border-ink-600 text-xs text-slate-300 space-y-2">
            <p>
              <strong>Effet sur la partie :</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-400 ml-1">
              <li>Trésorerie créditée de <strong className="text-emerald-300">+{recapAmount.toLocaleString('fr-FR')} €</strong></li>
              <li>Le compteur de cessation de paiements est remis à zéro</li>
              <li>Vous gagnez 30 jours supplémentaires pour redresser la situation</li>
              <li>Cette aide ne pourra plus être utilisée dans cette partie</li>
            </ul>
            <p className="pt-1 italic text-amber-300">
              Conseil apprenant : profitez-en pour réduire les charges fixes (vente d'un véhicule, négociation crédit, gel des embauches).
            </p>
          </div>

          <div className="p-3 rounded-lg bg-blue-950/40 border border-blue-500/40 text-xs text-slate-100">
            <strong className="text-blue-200">Trésorerie actuelle :</strong>{' '}
            <span className="font-mono">{treasury.toLocaleString('fr-FR')} €</span>{' '}
            → après recapitalisation :{' '}
            <span className="font-mono font-bold text-emerald-300">
              {(treasury + recapAmount).toLocaleString('fr-FR')} €
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-ink-600 flex items-center justify-end gap-2 bg-ink-900/40">
          <button
            type="button"
            onClick={decline}
            className="px-4 py-2 rounded-lg bg-ink-600 hover:bg-ink-500 text-slate-200 text-sm font-medium transition-colors"
            data-testid="age-decline"
          >
            Refuser — laisser le tribunal trancher
          </button>
          <button
            type="button"
            onClick={accept}
            className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors flex items-center gap-2"
            data-testid="age-accept"
          >
            <Wallet className="w-4 h-4" />
            Accepter — verser {recapAmount.toLocaleString('fr-FR')} €
          </button>
        </div>
      </div>
    </div>
  );
}
